import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormHelperText,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { getAdminCategories } from "../../../../services/Admin/ProductsAndCategoriesApi";

const isActiveCategory = (status) => status === 1 || status === "1" || status === true || status === "true";

const flattenCategories = (cats, level = 0) => {
  if (!Array.isArray(cats)) return [];
  let result = [];
  cats.forEach((cat) => {
    const children = Array.isArray(cat.children_recursive) ? cat.children_recursive : [];

    if (isActiveCategory(cat?.status)) {
      result.push({ ...cat, level });
      if (children.length > 0) {
        result = result.concat(flattenCategories(children, level + 1));
      }
    } else {
      // Parent is inactive: skip it, but still keep active descendants.
      if (children.length > 0) {
        result = result.concat(flattenCategories(children, level));
      }
    }
  });
  return result;
};

const EditProductDialog = ({ open, onClose, onSubmit, initialData = {} }) => {
  const [categoryId, setCategoryId] = useState("");
  const [name_ar, setNameAr] = useState("");
  const [name_en, setNameEn] = useState("");
  const [desc_ar, setDescAr] = useState("");
  const [desc_en, setDescEn] = useState("");
  const [quantity, setQuantity] = useState("");
  const [status, setStatus] = useState(true);
  const [discount, setDiscount] = useState("");
  const [retrun_status, setRetrunStatus] = useState(false);
  const [sale, setSale] = useState(false);
  const [return_policie_ar, setReturnPolicieAr] = useState("");
  const [return_policie_en, setReturnPolicieEn] = useState("");
  const [main_photo, setMainPhoto] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setCategoryId(initialData.category_id ?? "");
      setNameAr(initialData.name_ar ?? "");
      setNameEn(initialData.name_en ?? "");
      setDescAr(initialData.desc_ar ?? "");
      setDescEn(initialData.desc_en ?? "");
      setQuantity(initialData.quantity ?? "");
      setStatus(initialData.status === 1 || initialData.status === "1" || initialData.status === true);
      setDiscount(initialData.discount ?? "");
      setRetrunStatus(Boolean(initialData.retrun_status));
      setSale(Boolean(initialData.sale));
      setReturnPolicieAr(initialData.return_policie_ar ?? "");
      setReturnPolicieEn(initialData.return_policie_en ?? "");
      setMainPhoto(null);
      setError(null);
      setSubmitting(false);
    } else {
      setSubmitting(false);
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) return;

    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await getAdminCategories();
        const data = res?.data || res || [];
        setCategories(flattenCategories(Array.isArray(data) ? data : []));
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [open]);

  const handleSave = async () => {
    setError(null);
    if (!name_ar && !name_en) {
      setError("Please provide name_ar or name_en");
      return;
    }
    const formData = new FormData();
    // include id for update
    if (initialData.id) formData.append("id", String(initialData.id));
    formData.append("category_id", categoryId);
    formData.append("name_ar", name_ar);
    formData.append("name_en", name_en);
    formData.append("desc_ar", desc_ar);
    formData.append("desc_en", desc_en);
    formData.append("quantity", quantity);
    formData.append("status", status ? "1" : "0");
    formData.append("discount", discount);
    formData.append("retrun_status", retrun_status ? "1" : "0");
    formData.append("sale", sale ? "1" : "0");
    formData.append("return_policie_ar", return_policie_ar ?? "");
    formData.append("return_policie_en", return_policie_en ?? "");
    if (main_photo) formData.append("main_photo", main_photo);

    try {
      setSubmitting(true);
      const res = await onSubmit(formData);
      return res;
    } catch (err) {
      const serverData = err?.response?.data || err?.original?.response?.data;
      if (serverData) {
        const msgs = [];
        if (serverData.message) msgs.push(serverData.message);
        if (serverData.errors && typeof serverData.errors === "object") {
          Object.values(serverData.errors).forEach((v) => {
            if (Array.isArray(v)) msgs.push(...v);
            else msgs.push(v);
          });
        }
        setError(msgs.length ? msgs.join(" | ") : (err?.message || "Error updating product"));
      } else {
        setError(err?.message || "Error updating product");
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>تعديل المنتج</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="edit-product-category-label">الفئة</InputLabel>
            <Select
              labelId="edit-product-category-label"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              label="الفئة"
              disabled={loadingCategories}
            >
              <MenuItem value="">
                <em>-- اختر الفئة --</em>
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={String(cat.id)}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span style={{ marginLeft: cat.level * 20 }}>
                      {cat.level > 0 && "└─ "}
                      {cat.name_ar || cat.name_en}
                    </span>
                    <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
                      ({cat.name_en || cat.name_ar})
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {loadingCategories ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  جاري تحميل الفئات...
                </Typography>
              </Box>
            ) : null}
          </FormControl>
          <TextField label="الاسم (عربي)" value={name_ar} onChange={(e) => setNameAr(e.target.value)} fullWidth />
          <TextField label="الاسم (EN)" value={name_en} onChange={(e) => setNameEn(e.target.value)} fullWidth />
          <TextField label="الوصف (عربي)" value={desc_ar} onChange={(e) => setDescAr(e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="الوصف (EN)" value={desc_en} onChange={(e) => setDescEn(e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="الكمية" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          {/* <TextField label="الخصم" value={discount} onChange={(e) => setDiscount(e.target.value)} /> */}
          <TextField label="سياسة الإرجاع (عربي)" value={return_policie_ar} onChange={(e) => setReturnPolicieAr(e.target.value)} />
          <TextField label="سياسة الإرجاع (EN)" value={return_policie_en} onChange={(e) => setReturnPolicieEn(e.target.value)} />
          <FormControlLabel control={<Checkbox checked={retrun_status} onChange={(e) => setRetrunStatus(e.target.checked)} />} label="حالة الإرجاع" />
          <FormControlLabel control={<Checkbox checked={sale} onChange={(e) => setSale(e.target.checked)} />} label="تخفيض" />
          <FormControlLabel control={<Checkbox checked={status} onChange={(e) => setStatus(e.target.checked)} />} label="الحالة" />
          <Box>
            <input type="file" accept="image/*" onChange={(e) => setMainPhoto(e.target.files?.[0] || null)} />
            <FormHelperText>اختياري - main_photo</FormHelperText>
          </Box>
          {error ? <Typography color="error">{error}</Typography> : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => !submitting && onClose && onClose()}>إلغاء</Button>
        <Button variant="contained" onClick={handleSave} disabled={submitting}>
          {submitting ? <CircularProgress size={18} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProductDialog;
